import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Lock, KeyRound, ArrowLeft, Loader2, CheckCircle, Mail, ShieldCheck, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  // States: 'email_form', 'email_sent', 'checking_token', 'token_valid', 'token_invalid', 'resetting', 'success'
  const [status, setStatus] = useState(token ? 'checking_token' : 'email_form');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Check if token is valid when page loads with token
  useEffect(() => {
    if (!token) return;

    const checkToken = async () => {
      try {
        const response = await axios.get(`${API}/api/auth/check-reset-token?token=${token}`);
        if (response.data.valid) {
          setStatus('token_valid');
        }
      } catch (error) {
        setStatus('token_invalid');
        setErrorMessage(error.response?.data?.detail || 'Lien invalide ou expiré');
      }
    };

    checkToken();
  }, [token]);

  // Step 1: Request reset link
  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email });
      setStatus('email_sent');
      toast.success('Si cet email existe, un lien de réinitialisation a été envoyé');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Reset password with token (POST)
  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }
    
    setStatus('resetting');
    
    try {
      const response = await axios.post(`${API}/api/auth/reset-password`, {
        token: token,
        new_password: newPassword
      });
      
      if (response.data.success) {
        setStatus('success');
        toast.success('Mot de passe mis à jour avec succès !');
        setTimeout(() => navigate('/auth'), 2000);
      }
    } catch (error) {
      setStatus('token_invalid');
      setErrorMessage(error.response?.data?.detail || 'Lien invalide ou expiré');
      toast.error(error.response?.data?.detail || 'Lien invalide ou expiré');
    }
  };

  // Success state
  if (status === 'success') {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="glass-card rounded-2xl p-8">
            <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-12 h-12 text-emerald-500" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Mot de passe mis à jour !</h1>
            <p className="text-zinc-400 mb-6">Vous allez être redirigé vers la connexion...</p>
            <Loader2 className="w-6 h-6 text-emerald-500 mx-auto animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  // Email sent confirmation
  if (status === 'email_sent') {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="glass-card rounded-2xl p-8">
            <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-12 h-12 text-emerald-500" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Email envoyé !</h1>
            <p className="text-zinc-400 mb-6">
              Si l'adresse <strong>{email}</strong> est associée à un compte, vous recevrez un lien de réinitialisation.
            </p>
            <p className="text-zinc-500 text-sm mb-2">
              Le lien est valide pendant <strong>30 minutes</strong>.
            </p>
            <p className="text-zinc-500 text-sm mb-6">
              Vérifiez également vos spams si vous ne recevez pas l'email.
            </p>
            <Link to="/auth">
              <Button variant="outline" className="w-full border-zinc-600 text-zinc-300 hover:bg-zinc-800">
                Retour à la connexion
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Checking token state
  if (status === 'checking_token') {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="glass-card rounded-2xl p-8">
            <Loader2 className="w-16 h-16 text-emerald-500 mx-auto mb-4 animate-spin" />
            <h1 className="text-2xl font-bold text-white mb-2">Vérification en cours...</h1>
            <p className="text-zinc-400">Veuillez patienter</p>
          </div>
        </div>
      </div>
    );
  }

  // Token invalid state
  if (status === 'token_invalid') {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="glass-card rounded-2xl p-8">
            <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-12 h-12 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Lien invalide</h1>
            <p className="text-zinc-400 mb-6">{errorMessage}</p>
            <Link to="/reset-password">
              <Button className="w-full btn-primary mb-3">
                Demander un nouveau lien
              </Button>
            </Link>
            <Link to="/auth">
              <Button variant="outline" className="w-full border-zinc-600 text-zinc-300 hover:bg-zinc-800">
                Retour à la connexion
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Resetting state
  if (status === 'resetting') {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="glass-card rounded-2xl p-8">
            <Loader2 className="w-16 h-16 text-emerald-500 mx-auto mb-4 animate-spin" />
            <h1 className="text-2xl font-bold text-white mb-2">Mise à jour en cours...</h1>
            <p className="text-zinc-400">Veuillez patienter</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <Link to="/auth" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-6">
          <ArrowLeft className="w-4 h-4" />
          Retour à la connexion
        </Link>

        <div className="glass-card rounded-2xl p-8">
          {/* Logo */}
          <div className="flex items-center justify-center gap-2 mb-6">
            <div className="p-2 bg-emerald-500/20 rounded-xl">
              <Lock className="w-6 h-6 text-emerald-500" />
            </div>
            <span className="text-emerald-500 font-bold text-xl">YOUMI GREEN</span>
            <span className="text-white font-bold text-xl">VTC</span>
          </div>

          {/* Email form (step 1) */}
          {status === 'email_form' && (
            <>
              <div className="text-center mb-6">
                <h1 className="text-2xl font-bold text-white mb-2">Mot de passe oublié ?</h1>
                <p className="text-zinc-400 text-sm">
                  Entrez votre email pour recevoir un lien de réinitialisation
                </p>
              </div>

              <form onSubmit={handleRequestReset} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Adresse email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="votre@email.com"
                      required
                      className="pl-10 bg-zinc-900 border-zinc-700 text-white"
                      data-testid="reset-email-input"
                    />
                  </div>
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full btn-primary"
                  disabled={loading}
                  data-testid="send-reset-link-btn"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Envoyer le lien'
                  )}
                </Button>
              </form>
            </>
          )}

          {/* Password form (step 2 - token is valid) */}
          {status === 'token_valid' && (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ShieldCheck className="w-10 h-10 text-emerald-500" />
                </div>
                <h1 className="text-2xl font-bold text-white mb-2">Nouveau mot de passe</h1>
                <p className="text-zinc-400 text-sm">
                  Entrez votre nouveau mot de passe
                </p>
              </div>

              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Nouveau mot de passe</Label>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Minimum 8 caractères"
                      required
                      minLength={8}
                      className="pl-10 bg-zinc-900 border-zinc-700 text-white"
                      data-testid="new-password-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-zinc-300">Confirmer le mot de passe</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirmez votre mot de passe"
                      required
                      className="pl-10 bg-zinc-900 border-zinc-700 text-white"
                      data-testid="confirm-password-input"
                    />
                  </div>
                  {newPassword && confirmPassword && newPassword !== confirmPassword && (
                    <p className="text-xs text-red-400">Les mots de passe ne correspondent pas</p>
                  )}
                </div>

                <Button 
                  type="submit" 
                  className="w-full btn-primary"
                  disabled={loading || (newPassword !== confirmPassword)}
                  data-testid="reset-password-btn"
                >
                  Réinitialiser le mot de passe
                </Button>
              </form>
            </>
          )}
        </div>

        {/* Footer */}
        <p className="text-zinc-500 text-sm text-center mt-6">
          YOUMI GREEN VTC - Service VTC 100% électrique à Nice
        </p>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
